import { API_BASE_URL } from '../config/api';
import {
  migrateLegacyVoiceStorage,
  readVoiceKey,
  resolveVoiceStorageScope,
  writeVoiceKey,
} from '../voice/voiceStorage';
import { VOICE_STORAGE_SUFFIXES } from '../voice/constants';

export interface VoiceSettingsSnapshot {
  voice_mode?: 'full' | 'basic' | 'silent';
  voice_provider?: 'builtin' | 'elevenlabs' | 'custom';
  selected_voice?: string;
  elevenlabs_api_key?: string;
  has_elevenlabs_api_key?: boolean;
  elevenlabs_voice_id?: string;
  custom_voice_id?: string;
  default_language?: string;
}

function ensureScopeMigrated() {
  const scope = resolveVoiceStorageScope();
  if (scope) migrateLegacyVoiceStorage(scope);
  return scope;
}

export const readCachedVoiceSettings = (): VoiceSettingsSnapshot => {
  try {
    const scope = ensureScopeMigrated();
    if (scope) {
      const scoped = readVoiceKey<VoiceSettingsSnapshot>(
        scope,
        VOICE_STORAGE_SUFFIXES.settings
      );
      if (scoped) return scoped;
    }
    // Fallback to legacy flat key during pre-auth / migration edge cases
    const raw = localStorage.getItem('voiceSettings');
    if (raw) return JSON.parse(raw) as VoiceSettingsSnapshot;
  } catch {
    /* ignore malformed cache */
  }
  return {};
};

export const writeCachedVoiceSettings = (settings: VoiceSettingsSnapshot): void => {
  const scope = resolveVoiceStorageScope();
  if (scope) {
    writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.settings, settings);
  }
  // Keep legacy key in sync for components that still read it before scope is known
  try {
    localStorage.setItem('voiceSettings', JSON.stringify(settings));
  } catch {
    /* ignore */
  }
};

/** Load voice settings from cache, falling back to the server when needed. */
export const getVoiceSettings = async (
  forceRefresh = false
): Promise<VoiceSettingsSnapshot> => {
  if (!forceRefresh) {
    const cached = readCachedVoiceSettings();
    if (cached.voice_provider) return cached;
  }

  try {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return readCachedVoiceSettings();

    const response = await fetch(`${API_BASE_URL}/settings/voice`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.ok) {
      const data = await response.json();
      const settings = (data.data?.voiceSettings || {}) as VoiceSettingsSnapshot;
      writeCachedVoiceSettings(settings);
      return settings;
    }
  } catch (err) {
    console.warn('Failed to fetch voice settings:', err);
  }

  return readCachedVoiceSettings();
};

export const preloadVoiceSettings = async (): Promise<void> => {
  await getVoiceSettings();
};
